import React, { useState, useEffect, useCallback } from 'react';
import ComputerPlayer from './ComputerPlayer.js';

// Atari Go Game Component
/**
 * Atari Go: A two-player strategy game on a 9x9 Go board
 *
 * Game Rules:
 * - Players alternate placing stones (Player 1: Black, Player 2: White)
 * - A stone or connected group is captured when all its liberties are occupied
 * - The first player to capture at least one opponent stone wins
 * - Atari: a group with exactly one liberty is in danger of capture
 * - Ko rule: a move that repeats the immediately previous board state is illegal
 */
const AtariGo = () => {
	const [gameStarted, setGameStarted] = useState(false);
	const [playerTypes, setPlayerTypes] = useState({ 1: 'human', 2: 'human' });
	const [useCrossCut, setUseCrossCut] = useState(false);
	const [board, setBoard] = useState(Array(81).fill(null)); // 9x9 = 81 intersections
	const [currentPlayer, setCurrentPlayer] = useState(1); // 1 = Black, 2 = White
	const [winner, setWinner] = useState(null);
	const [lastBoard, setLastBoard] = useState(null); // for Ko rule
	const [computerPlayer] = useState(new ComputerPlayer());

	/**
	 * Convert row and column to board index
	 * @param {number} row - Row number (0-8)
	 * @param {number} col - Column number (0-8)
	 * @returns {number} - Board index
	 */
	const coordsToIndex = (row, col) => row * 9 + col;

	/**
	 * Convert board index to row and column
	 * @param {number} index - Board index
	 * @returns {Object} - {row, col}
	 */
	const indexToCoords = (index) => ({
		row: Math.floor(index / 9),
		col: index % 9
	});

	/**
	 * Get the orthogonal neighbours of a cell
	 * @param {number} row - Row number
	 * @param {number} col - Column number
	 * @returns {Array<number>} - Array of neighbour indices
	 */
	const getNeighbors = (row, col) => {
		const neighbors = [];
		if (row > 0) neighbors.push(coordsToIndex(row - 1, col));
		if (row < 8) neighbors.push(coordsToIndex(row + 1, col));
		if (col > 0) neighbors.push(coordsToIndex(row, col - 1));
		if (col < 8) neighbors.push(coordsToIndex(row, col + 1));
		return neighbors;
	};

	/**
	 * BFS to find all stones of the same colour connected to the given index
	 * @param {Array} currentBoard - The current board state
	 * @param {number} index - Starting index
	 * @returns {Set<number>} - Set of indices in the group
	 */
	const getGroup = (currentBoard, index) => {
		const color = currentBoard[index];
		if (!color) return new Set();
		const group = new Set();
		const queue = [index];
		group.add(index);
		while (queue.length > 0) {
			const current = queue.shift();
			const { row, col } = indexToCoords(current);
			for (const neighbor of getNeighbors(row, col)) {
				if (!group.has(neighbor) && currentBoard[neighbor] === color) {
					group.add(neighbor);
					queue.push(neighbor);
				}
			}
		}
		return group;
	};

	/**
	 * Get the set of empty intersections adjacent to the group (liberties)
	 * @param {Array} currentBoard - The current board state
	 * @param {Set<number>} group - Set of indices in the group
	 * @returns {Set<number>} - Set of liberty indices
	 */
	const getLiberties = (currentBoard, group) => {
		const liberties = new Set();
		for (const index of group) {
			const { row, col } = indexToCoords(index);
			for (const neighbor of getNeighbors(row, col)) {
				if (currentBoard[neighbor] === null) {
					liberties.add(neighbor);
				}
			}
		}
		return liberties;
	};

	/**
	 * Check if the group containing the given index is in Atari (exactly 1 liberty)
	 * @param {Array} currentBoard - The current board state
	 * @param {number} index - Index to check
	 * @returns {boolean} - True if in Atari
	 */
	const isInAtari = (currentBoard, index) => {
		if (!currentBoard[index]) return false;
		const group = getGroup(currentBoard, index);
		return getLiberties(currentBoard, group).size === 1;
	};

	/**
	 * Remove all opponent groups with zero liberties and return capture count
	 * @param {Array} currentBoard - The board after a stone was placed
	 * @param {number} opponentColor - 1 or 2
	 * @returns {{ newBoard: Array, capturedCount: number }}
	 */
	const performCaptures = (currentBoard, opponentColor) => {
		const newBoard = [...currentBoard];
		let capturedCount = 0;
		const visited = new Set();

		for (let i = 0; i < 81; i++) {
			if (newBoard[i] === opponentColor && !visited.has(i)) {
				const group = getGroup(newBoard, i);
				group.forEach(idx => visited.add(idx));
				if (getLiberties(newBoard, group).size === 0) {
					capturedCount += group.size;
					group.forEach(idx => { newBoard[idx] = null; });
				}
			}
		}
		return { newBoard, capturedCount };
	};

	/**
	 * Check if newBoard is identical to lastBoard (Ko rule)
	 * @param {Array} newBoard - Board after the move
	 * @param {Array|null} prevBoard - Board before the previous move
	 * @returns {boolean} - True if the move would repeat the previous position
	 */
	const isKo = (newBoard, prevBoard) => {
		if (!prevBoard) return false;
		for (let i = 0; i < 81; i++) {
			if (newBoard[i] !== prevBoard[i]) return false;
		}
		return true;
	};

	/**
	 * Handle a click on a board intersection
	 * @param {number} row - Row of clicked intersection
	 * @param {number} col - Column of clicked intersection
	 */
	const handleClick = useCallback((row, col) => {
		const index = coordsToIndex(row, col);

		if (winner || board[index] !== null) return;

		// Place stone
		const newBoard = [...board];
		newBoard[index] = currentPlayer;

		// Capture opponent groups with no liberties
		const opponentColor = currentPlayer === 1 ? 2 : 1;
		const { newBoard: boardAfterCapture, capturedCount } = performCaptures(newBoard, opponentColor);

		// Ko rule: reject move if it recreates the previous board state
		if (isKo(boardAfterCapture, lastBoard)) return;

		setLastBoard(board);
		setBoard(boardAfterCapture);

		if (capturedCount > 0) {
			setWinner(currentPlayer);
		} else {
			setCurrentPlayer(opponentColor);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [board, currentPlayer, winner, lastBoard]);

	/**
	 * Reset the game to the configuration screen
	 */
	const resetGame = () => {
		setBoard(Array(81).fill(null));
		setCurrentPlayer(1);
		setWinner(null);
		setLastBoard(null);
		setGameStarted(false);
	};

	/**
	 * Start the game, optionally with the cross-cut formation
	 */
	const startGame = () => {
		if (useCrossCut) {
			const initial = Array(81).fill(null);
			// Cross-cut formation at centre: (3,3)=Black, (3,5)=White, (5,3)=White, (5,5)=Black
			initial[coordsToIndex(3, 3)] = 1;
			initial[coordsToIndex(3, 5)] = 2;
			initial[coordsToIndex(5, 3)] = 2;
			initial[coordsToIndex(5, 5)] = 1;
			setBoard(initial);
		} else {
			setBoard(Array(81).fill(null));
		}
		setGameStarted(true);
	};

	/**
	 * Handle player type selection
	 */
	const handlePlayerTypeChange = (player, type) => {
		setPlayerTypes(prev => ({ ...prev, [player]: type }));
	};

	// Computer player: pick a random valid (non-Ko) empty cell
	useEffect(() => {
		if (!gameStarted || winner) return;
		if (playerTypes[currentPlayer] !== 'computer') return;

		const timeoutId = setTimeout(() => {
			// Gather all valid moves
			const validMoves = [];
			for (let i = 0; i < 81; i++) {
				if (board[i] !== null) continue;
				const { row, col } = indexToCoords(i);
				const testBoard = [...board];
				testBoard[i] = currentPlayer;
				const opponentColor = currentPlayer === 1 ? 2 : 1;
				const { newBoard: boardAfterCapture } = performCaptures(testBoard, opponentColor);
				if (!isKo(boardAfterCapture, lastBoard)) {
					validMoves.push({ row, col });
				}
			}
			if (validMoves.length > 0) {
				const move = validMoves[Math.floor(Math.random() * validMoves.length)];
				handleClick(move.row, move.col);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPlayer, gameStarted, winner, board, playerTypes, computerPlayer, handleClick]);

	/**
	 * Get current game status message
	 * @returns {string}
	 */
	const getStatus = () => {
		if (winner) {
			const name = winner === 1 ? 'Pretas' : 'Brancas';
			return `Vencedor: ${name}!`;
		}
		const name = currentPlayer === 1 ? 'Pretas' : 'Brancas';
		return `Turno: ${name}`;
	};

	// SVG board constants
	const CELL_SIZE = 44;
	const PADDING = 30;
	const BOARD_PX = PADDING * 2 + CELL_SIZE * 8; // 382
	const STONE_RADIUS = 18;
	const STAR_RADIUS = 4;

	// Star points on a 9x9 board (0-indexed)
	const STAR_POINTS = [
		[2, 2], [2, 6],
		[4, 4],
		[6, 2], [6, 6]
	];

	/**
	 * Render the 9x9 SVG board
	 */
	const renderBoard = () => {
		return (
			<svg
				viewBox={`0 0 ${BOARD_PX} ${BOARD_PX}`}
				className="atari-go-board"
				onClick={(e) => {
					const target = e.target;
					if (target.hasAttribute('data-row')) {
						const row = parseInt(target.getAttribute('data-row'), 10);
						const col = parseInt(target.getAttribute('data-col'), 10);
						handleClick(row, col);
					}
				}}
			>
				{/* Board background */}
				<rect x={0} y={0} width={BOARD_PX} height={BOARD_PX} fill="#dcb468" rx={6} />

				{/* Grid lines */}
				{Array.from({ length: 9 }, (_, i) => {
					const pos = PADDING + i * CELL_SIZE;
					return (
						<g key={`grid-${i}`}>
							<line
								x1={PADDING} y1={pos}
								x2={PADDING + 8 * CELL_SIZE} y2={pos}
								stroke="#7a5c2e" strokeWidth={1}
							/>
							<line
								x1={pos} y1={PADDING}
								x2={pos} y2={PADDING + 8 * CELL_SIZE}
								stroke="#7a5c2e" strokeWidth={1}
							/>
						</g>
					);
				})}

				{/* Star points (hoshi) */}
				{STAR_POINTS.map(([r, c]) => (
					<circle
						key={`star-${r}-${c}`}
						cx={PADDING + c * CELL_SIZE}
						cy={PADDING + r * CELL_SIZE}
						r={STAR_RADIUS}
						fill="#7a5c2e"
						pointerEvents="none"
					/>
				))}

				{/* Stones and hit areas */}
				{Array.from({ length: 9 }, (_, row) =>
					Array.from({ length: 9 }, (_, col) => {
						const index = coordsToIndex(row, col);
						const stone = board[index];
						const cx = PADDING + col * CELL_SIZE;
						const cy = PADDING + row * CELL_SIZE;
						const atari = stone ? isInAtari(board, index) : false;

						return (
							<g key={`cell-${row}-${col}`}>
								{/* Stone */}
								{stone === 1 && (
									<>
										{atari && (
											<circle cx={cx} cy={cy} r={STONE_RADIUS + 4}
												fill="none" stroke="#e53935" strokeWidth={2.5} opacity={0.85} pointerEvents="none" />
										)}
										<circle cx={cx} cy={cy} r={STONE_RADIUS}
											fill="#111" stroke="#000" strokeWidth={1} pointerEvents="none" />
									</>
								)}
								{stone === 2 && (
									<>
										{atari && (
											<circle cx={cx} cy={cy} r={STONE_RADIUS + 4}
												fill="none" stroke="#e53935" strokeWidth={2.5} opacity={0.85} pointerEvents="none" />
										)}
										<circle cx={cx} cy={cy} r={STONE_RADIUS}
											fill="#fff" stroke="#333" strokeWidth={1.5} pointerEvents="none" />
									</>
								)}

								{/* Clickable hit area (transparent, only on empty cells) */}
								{!stone && !winner && (
									<circle
										cx={cx} cy={cy}
										r={STONE_RADIUS}
										fill="transparent"
										style={{ cursor: 'pointer' }}
										data-row={row}
										data-col={col}
									/>
								)}
							</g>
						);
					})
				)}
			</svg>
		);
	};

	// ── Configuration screen ─────────────────────────────────────────────────
	if (!gameStarted) {
		return (
			<div className="game-container">
				<h1 className="game-title">Atari Go</h1>
				<h2 className="config-subtitle">Configuração dos Jogadores</h2>
				<div className="player-config">
					<div className="player-config-item">
						<h3>Jogador 1 (Pretas)</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes[1] === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(1, 'human')}
							>
								Humano
							</button>
							<button
								className={`player-type-button ${playerTypes[1] === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(1, 'computer')}
							>
								Computador
							</button>
						</div>
					</div>
					<div className="player-config-item">
						<h3>Jogador 2 (Brancas)</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes[2] === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(2, 'human')}
							>
								Humano
							</button>
							<button
								className={`player-type-button ${playerTypes[2] === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(2, 'computer')}
							>
								Computador
							</button>
						</div>
					</div>
					<div className="player-config-item">
						<h3>Posição Inicial</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${!useCrossCut ? 'selected' : ''}`}
								onClick={() => setUseCrossCut(false)}
							>
								Tabuleiro Vazio
							</button>
							<button
								className={`player-type-button ${useCrossCut ? 'selected' : ''}`}
								onClick={() => setUseCrossCut(true)}
							>
								Cruzamento
							</button>
						</div>
					</div>
				</div>
				<button className="new-game-button" onClick={startGame}>
					Iniciar Jogo
				</button>
			</div>
		);
	}

	// ── Game screen ──────────────────────────────────────────────────────────
	return (
		<div className="game-container atari-go-game-container">
			<h1 className="game-title">Atari Go</h1>
			<div className={`game-status ${winner ? 'winner' : ''}`}>
				{getStatus()}
			</div>
			{renderBoard()}
			<button className="new-game-button" onClick={resetGame}>
				Novo Jogo
			</button>
		</div>
	);
};

export default AtariGo;
